import * as React from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import classNames from 'classnames';
import { MultipleSelectFilter, getIcon } from '@quality24/design-system';
import isEqual from 'lodash/isEqual';

import {
  FilterComponentData,
  FilterUsageOption,
  GraphQLFilter,
} from '../../../../@types';
import { useQueryFilter } from '../../../../hooks/filterHooks';
import { parseDataValue } from '../../../../utils/dataUtils';
import {
  parseFilterData,
  formatFilterData,
  mergePermissionFilter,
} from '../../../../utils/filterUtils';

const MockQuery = gql`
  query MockQueryNotToBePerformed {
    apiVersion {
      versionName
    }
  }
`;

export interface Props {
  className?: string;
  partitionId?: 'default' | string;
  config: FilterComponentData;
  baseFilter?: GraphQLFilter;
  permissionFilter?: GraphQLFilter;
}

/**
 * <MultipleSelectFilterContainer> page
 */
export const MultipleSelectFilterContainer: React.FunctionComponent<Props> = ({
  className,
  partitionId = 'default',
  baseFilter = {},
  permissionFilter: userPermissionFilter = {},
  config: {
    id,
    className: configClassName,
    label,
    type = 'checkbox',
    icon,
    innerFilters,
    customFilter,
    permissionFilters,
    query,
    parser,
    parserOptions,
    options,
    searchable,
  },
}) => {
  const { filters, upsertFilter, removeFilter } = useQueryFilter(
    [id],
    partitionId,
  );

  // Handle inner filters
  const { getGraphQLFilter, getPermissionFilter } = useQueryFilter(
    innerFilters?.map((f) => (typeof f === 'string' ? f : f.id)) || [],
    partitionId,
  );

  // Retrieve and memoize the graphQLFilters
  // This getter applies any usage modifications
  const graphQLFilter = React.useMemo(
    () =>
      getGraphQLFilter(
        innerFilters?.filter(
          (f) => typeof f !== 'string',
        ) as FilterUsageOption[],
      ),
    [getGraphQLFilter, innerFilters],
  );

  // Merge all filters for the query
  const queryFilter = React.useMemo(() => {
    if (
      (baseFilter && Object.keys(baseFilter).length > 0) ||
      (graphQLFilter && Object.keys(graphQLFilter).length > 0) ||
      (permissionFilters && permissionFilters.length > 0) ||
      (customFilter && Object.keys(customFilter).length > 0)
    ) {
      const permissionFilter = getPermissionFilter(
        userPermissionFilter,
        permissionFilters,
      );

      const parsedFilters = mergePermissionFilter(
        {
          ...baseFilter,
          ...graphQLFilter,
          ...customFilter,
        },
        permissionFilter,
      );

      return Object.keys(parsedFilters).length ? parsedFilters : undefined;
    }
    return undefined;
  }, [
    baseFilter,
    customFilter,
    getPermissionFilter,
    graphQLFilter,
    permissionFilters,
    userPermissionFilter,
  ]);

  // Prepare the query to eventually fetch filter
  const [fetchData, { data, previousData, loading, called }] = useLazyQuery(
    query ? gql([query]) : MockQuery,
    { variables: { filter: queryFilter } },
  );

  // Do the query after render if any filter is selected
  React.useEffect(() => {
    const filterValue = filters[id];
    if (filterValue && query && !called) {
      fetchData();
    }
  }, [filters, id, query, fetchData, called]);

  // Parse query filter format
  const value = React.useMemo(() => {
    const filterValue = filters[id];
    if (filterValue) {
      const parsed = parseFilterData(parser, filterValue, { parserOptions });
      if (parsed) return parsed;
    }
    return type === 'checkbox' ? [] : '';
  }, [filters, type, parser, parserOptions, id]);

  // Build the filter options.
  // Fall back to `previousData` while options are being refetched, so the
  // trigger keeps showing the previous options instead of rendering blank.
  const filterOptions = React.useMemo(() => {
    const sourceData = data ?? previousData;
    return options
      .flatMap((opt) => {
        if (!opt.rootKey) return opt;
        if (!sourceData) return null;

        return sourceData[opt.rootKey][opt.nodesKey || 'nodes'].map(
          (n: Record<string, unknown>) => ({
            key: opt.key
              ? parseDataValue(n, opt.key, opt.dataType || 'string')
              : undefined,
            id: parseDataValue(n, opt.id, opt.dataType || 'string'),
            label: parseDataValue(n, opt.label, opt.dataType || 'string'),
          }),
        );
      })
      .filter((opt) => opt);
  }, [options, data, previousData]);

  /**
   * Handles the filter change, saving it the the context
   */
  const handleFilterChange = React.useCallback(
    (name: string, newValue: string | string[]) => {
      const isEmptyArray =
        Array.isArray(newValue) &&
        (newValue.length === 0 || isEqual(newValue, ['']));
      if (isEmptyArray || newValue === '') {
        removeFilter(name);
        return;
      }

      const formatted = formatFilterData(parser, newValue, { parserOptions });
      if (formatted) {
        upsertFilter(name, formatted);
      }
    },
    [upsertFilter, removeFilter, parser, parserOptions],
  );

  /**
   * When an inner (parent) filter changes, this filter's options are refetched
   * and may no longer contain the selected values (e.g. removing a floor drops
   * its rooms). Prune those orphaned values so the filter resets to "Todos"
   * instead of getting stuck filtering by an invisible selection.
   *
   * `!data || loading` skips in-flight/first fetches, running only against
   * settled options. Layout effect so pruning is flushed before paint.
   */
  React.useLayoutEffect(() => {
    if (!innerFilters?.length || !query || !data || loading) return;
    if (type !== 'checkbox' || !Array.isArray(value) || value.length === 0) {
      return;
    }

    const validIds = new Set(filterOptions.map((opt) => opt?.id));
    const prunedValue = value.filter((v) => validIds.has(v));

    if (prunedValue.length !== value.length) {
      handleFilterChange(id, prunedValue);
    }
  }, [
    innerFilters,
    query,
    data,
    loading,
    type,
    value,
    filterOptions,
    id,
    handleFilterChange,
  ]);

  // Add log for debuging with DVC-Designer
  if (process.env.REACT_APP_HOST_PROJECT === 'designer') {
    // eslint-disable-next-line no-console
    console.info('MultipleSelectFilterContainer', {
      filterContext: filters,
    });
  }

  return (
    <MultipleSelectFilter
      className={classNames(className, configClassName)}
      name={id}
      label={label}
      icon={icon ? getIcon(icon) : undefined}
      options={filterOptions}
      loading={loading}
      onOpen={() => {
        if (query) fetchData();
      }}
      onChange={handleFilterChange}
      withSearch={searchable}
      // Workaround: I had to do this because of an error with the TS
      // It prevented the dynamic types accordingly to the 'type' field
      // to work out.
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(type === 'checkbox'
        ? {
            type: 'checkbox',
            value: value as string[],
          }
        : {
            type,
            value: value as string,
          })}
    />
  );
};

export default MultipleSelectFilterContainer;
